import { useCallback, useEffect, useState } from 'react'

import { useLazyCheckSegmentNameQuery } from 'api/segments'
import ThanxAIPromptInput from 'components/AI/ThanxAIPromptInput'
import ModalContainer from 'components/ModalContainer'
import Formsy from 'formsy-react'
import { buildTranslate } from 'locales'
import PreferenceBanner from './PreferenceBanner'
import './ThanxAISegmentNameModal.scss'

const t = buildTranslate('components.ai.segment_name_modal')

export interface ThanxAISegmentNameModalProps {
  isOpen: boolean
  initialValue: string
  onSubmit: (name: string) => void
  onCancel: () => void
  showPreferenceBanner: boolean
  preference: { autoApprove: boolean } | null
  onChangePreferenceClicked: () => void
  onNameChange?: (name: string) => void
}

const ThanxAISegmentNameModal = ({
  isOpen,
  initialValue,
  onSubmit,
  onCancel,
  showPreferenceBanner,
  preference,
  onChangePreferenceClicked,
  onNameChange,
}: ThanxAISegmentNameModalProps) => {
  const [canSubmit, setCanSubmit] = useState(false)
  const [validationError, setValidationError] = useState<string>('')
  const [isValidating, setIsValidating] = useState(false)
  const [checkSegmentName] = useLazyCheckSegmentNameQuery()

  useEffect(() => {
    setCanSubmit(!!initialValue.trim())
    setValidationError('')
  }, [isOpen, initialValue])

  const handleValid = useCallback(() => {
    setCanSubmit(true)
  }, [])

  const handleInvalid = useCallback(() => {
    setCanSubmit(false)
  }, [])

  const validateSegmentName = useCallback(
    async (name: string): Promise<boolean> => {
      setIsValidating(true)
      setValidationError('')

      try {
        const result = await checkSegmentName({ name }).unwrap()

        if (!result.available) {
          setValidationError(t('validation_error_duplicate'))
          return false
        }

        return true
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          'Error checking segment name availability:',
          error,
          'Name attempted:',
          name
        )
        setValidationError(t('validation_error_api_failure'))
        return false
      } finally {
        setIsValidating(false)
      }
    },
    [checkSegmentName]
  )

  const handleSubmit = useCallback(
    async (formData: { segmentName: string }) => {
      if (!formData.segmentName?.trim()) {
        return
      }

      const trimmedName = formData.segmentName.trim()

      const isValid = await validateSegmentName(trimmedName)
      if (!isValid) {
        return
      }

      onSubmit(trimmedName)
    },
    [onSubmit, validateSegmentName]
  )

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onCancel}
      title={t('title')}
      subtitle={t('subtitle')}
      width="600px"
      hideCancel
      showBottomProceed={false}
      className="thanx-ai-segment-name-modal"
      zIndex={1000}
      dialogStyle={{
        borderRadius: '4px',
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px',
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px',
      }}
    >
      <div className="thanx-ai-segment-name-modal__input-wrapper">
        <Formsy
          onValidSubmit={handleSubmit}
          onValid={handleValid}
          onInvalid={handleInvalid}
          onChange={(values: { segmentName?: string }) => {
            onNameChange?.(values.segmentName || '')
          }}
          className="thanx-ai-segment-name-modal__form"
          id="thanx-ai-segment-name-form"
        >
          <ThanxAIPromptInput
            name="segmentName"
            placeholder={t('input_placeholder')}
            submitText={t('save_button')}
            initialValue={initialValue}
            id="segment-name-input"
            required
            validations={{
              isNotEmpty: (values: any, value: string) => {
                return value && value.trim().length > 0
              },
            }}
            validationErrors={{
              isNotEmpty: t('validation_error'),
            }}
          />
          {validationError && (
            <div
              className="thanx-ai-segment-name-modal__validation-error"
              role="alert"
            >
              {validationError}
            </div>
          )}
        </Formsy>
      </div>

      {showPreferenceBanner && preference !== null && (
        <PreferenceBanner
          preference={preference}
          onChangeClicked={onChangePreferenceClicked}
        />
      )}

      <div className="thanx-ai-segment-name-modal__actions">
        <button
          type="button"
          onClick={onCancel}
          className="thanx-ai-segment-name-modal__button thanx-ai-segment-name-modal__button--secondary"
        >
          {t('go_back_button')}
        </button>
        <button
          type="submit"
          form="thanx-ai-segment-name-form"
          disabled={!canSubmit || isValidating}
          className="thanx-ai-segment-name-modal__button thanx-ai-segment-name-modal__button--primary"
        >
          {t('save_button')}
        </button>
      </div>
    </ModalContainer>
  )
}

export default ThanxAISegmentNameModal
